import { useI18nContext } from "@hopper-b2b/i18n";
import { SliceStopCountFilter, TripCategory } from "@hopper-b2b/types";
import { ActionButton, ActionLink } from "@hopper-b2b/ui";
import {
  useApiConfigSelector,
  useDeviceTypes,
  useEnablePricePrediction,
} from "@hopper-b2b/utilities";
import { Box } from "@material-ui/core";
import clsx from "clsx";
import { isEqual } from "lodash-es";
import { useCallback, useMemo, useState } from "react";

import { flightApiConfigStoreKey } from "../../../../reducers/types";
import {
  FareclassOptionFilter,
  initialFilterOptions,
} from "../../reducer/index";
import { MobilePredictionFlightSearchFilter } from "./components";
import { FlightSearchFilterConnectorProps } from "./container";
import "./styles.scss";

export interface IFlightSearchFilterProps
  extends FlightSearchFilterConnectorProps {
  openModal: boolean;
  onClose: () => void;
  onFiltersApplied: () => void;
  tripCategory: TripCategory;
}

export const FlightSearchFilter = (props: IFlightSearchFilterProps) => {
  const {
    openModal,
    onClose,
    currentStopsOption,
    currentFareclassOptionFilter,
    readyToReset,
    setCurrentStopsOption,
    setCurrentFareclassOptionFilter,
    onFiltersApplied,
    setRerunPrediction,
  } = props;

  const { matchesMobile } = useDeviceTypes();

  const { t } = useI18nContext();

  const enablePricePrediction = useEnablePricePrediction();

  const [stopsOption, setStopsOption] =
    useState<SliceStopCountFilter>(currentStopsOption);
  const [fareclassOptionFilter, setFareclassOptionFilter] =
    useState<FareclassOptionFilter>(currentFareclassOptionFilter);
  const optionsChanged = useMemo(() => {
    return (
      stopsOption !== currentStopsOption ||
      !isEqual(fareclassOptionFilter, currentFareclassOptionFilter)
    );
  }, [
    stopsOption,
    currentStopsOption,
    fareclassOptionFilter,
    currentFareclassOptionFilter,
  ]);
  const apiConfig = useApiConfigSelector(flightApiConfigStoreKey);

  const handleApply = useCallback(() => {
    setCurrentStopsOption(stopsOption, apiConfig);
    setCurrentFareclassOptionFilter(fareclassOptionFilter);
    onFiltersApplied();
  }, [
    setCurrentStopsOption,
    stopsOption,
    apiConfig,
    setCurrentFareclassOptionFilter,
    fareclassOptionFilter,
    onFiltersApplied,
  ]);

  const handleReset = useCallback(() => {
    setStopsOption(initialFilterOptions.stopsOption);
    setFareclassOptionFilter({
      basic: false,
      standard: false,
      enhanced: false,
      premium: false,
      luxury: false,
    });
    enablePricePrediction && setRerunPrediction();
  }, [enablePricePrediction, setRerunPrediction]);

  const renderFilterButtons = useCallback(() => {
    return (
      <Box
        className={clsx("flight-search-filter-bottom-buttons-container", {
          mobile: matchesMobile,
        })}
      >
        <ActionLink
          className={clsx("reset-filters-link", "filter-button", {
            inactive: !readyToReset && !optionsChanged,
          })}
          onClick={handleReset}
          content={t("searchFilter.reset")}
        />
        <ActionButton
          className={clsx("apply-filters-button", "filter-button")}
          onClick={() => {
            handleApply();
            onClose();
          }}
          disabled={!optionsChanged}
          message={t("searchFilter.apply")}
        />
      </Box>
    );
  }, [optionsChanged, readyToReset, handleReset, handleApply, onClose]);

  return (
    <MobilePredictionFlightSearchFilter
      openModal={openModal}
      onClose={onClose}
      stopsOption={stopsOption}
      setStopsOption={setStopsOption}
      fareclassOptionFilter={fareclassOptionFilter}
      setFareclassOptionFilter={setFareclassOptionFilter}
      bottomButton={renderFilterButtons()}
    />
  );
};
